import React, {
  Context,
  createContext,
  Reducer,
  useCallback,
  useContext,
  useReducer,
} from 'react';
import { Action } from '../../hooks/action';

/**
 * Type d'action pour les modifications de groupe d'utilisateurs
 */
type UserGroupAction = Action<Partial<CRM.UserGroup>>;

/**
 * Interface du contexte de groupe d'utilisateurs
 */
interface UserGroupContext {
  userGroup: Partial<CRM.UserGroupDocument>;
  update: (payload: Partial<CRM.UserGroup>) => void;
  save: () => void;
}

/**
 * Contexte de gestion des groupes d'utilisateurs
 *
 * Ce contexte gère l'état et les actions des groupes d'utilisateurs :
 * - État du groupe en cours de modification
 * - Fonctions de mise à jour et sauvegarde
 * - Reducer pour les modifications d'état
 *
 * Logique métier :
 * - Gestion centralisée de l'état du groupe
 * - Actions de type "update" pour les modifications
 * - Callback de sauvegarde personnalisé
 */
const userGroupContext: Context<UserGroupContext> = createContext(null) as any;

/**
 * Hook pour accéder au contexte de groupe d'utilisateurs
 *
 * @returns Contexte avec groupe, fonctions de mise à jour et sauvegarde
 */
export function useUserGroup(): UserGroupContext {
  const context = useContext(userGroupContext);
  return context;
}

/**
 * Reducer pour les modifications de groupe d'utilisateurs
 *
 * Gère les actions de type "update" pour fusionner les nouvelles données
 * avec l'état existant du groupe.
 *
 * @param state État actuel du groupe
 * @param action Action à traiter
 * @returns Nouvel état du groupe
 */
const reducer: Reducer<Partial<CRM.UserGroup>, UserGroupAction> = (
  state,
  action,
) => {
  console.log('ACTION', action.type, action.payload);
  switch (action.type) {
    case 'update':
      return {
        ...state,
        ...action.payload,
      };
  }
  return state;
};

/**
 * Interface pour les props du provider de groupe d'utilisateurs
 */
interface Props {
  initialUserGroup?: CRM.UserGroup;
  onSave: (userGroup: CRM.UserGroup) => void;
}

/**
 * Provider du contexte de groupe d'utilisateurs
 *
 * Ce provider :
 * 1. Initialise l'état du groupe avec useReducer
 * 2. Fournit les fonctions de mise à jour et sauvegarde
 * 3. Gère les callbacks de sauvegarde personnalisés
 *
 * @param initialUserGroup Groupe initial (pour modification)
 * @param onSave Callback de sauvegarde personnalisé
 * @param children Composants enfants
 * @returns Provider React du contexte de groupe d'utilisateurs
 */
export const ProvideUserGroup: React.FC<Props> = ({
  onSave,
  initialUserGroup,
  children,
}) => {
  const [userGroup, dispatch] = useReducer(reducer, initialUserGroup || {});

  const update = useCallback((payload: Partial<CRM.UserGroup>) => {
    dispatch({ type: 'update', payload });
  }, []);

  const save = useCallback(() => {
    onSave(userGroup as CRM.UserGroup);
  }, [onSave, userGroup]);

  return (
    <userGroupContext.Provider value={{ userGroup, update, save }}>
      {children}
    </userGroupContext.Provider>
  );
};
